'use client';

import {useEffect, useRef, useState, useMemo} from 'react';
import JSZip from 'jszip';
import {captureException} from '@sentry/react';
import {toast} from 'sonner';

import amplitudeService from '@features/analytics/amplitudeService';
import {cn} from '@features/ui/utils';
import {Checkbox} from '@features/ui/components/Checkbox';
import {trpc} from '@features/utils/trpc';
import {Dialog, DialogContent} from '@features/ui/components/dialog';
import {Button} from '@features/ui/components/Button';
import {useExportToFigmaMulti} from './useExportToFigmaMulti';
import {useSubscription} from '@features/subscription/subscriptionHook';
import {usePaywallModal} from '@features/subscription/PaywallModal';

type ExportType = 'png' | 'jpeg' | 'figma';

const useScreenNames = (chatIds: string[]) => {
  const queries = trpc.useQueries((t) =>
    chatIds.map((chatId) => t.screens.getScreen({screenId: chatId})),
  );

  const entries = queries.map(
    ({data}) => data && ([data.id, data.screenName] as const),
  );

  const screenNames = new Map(entries.filter((entry) => entry !== undefined));

  return {screenNames};
};

function FigmaExportButton({
  exportType,
  isLoading,
  isDisabled,
  isPaidUser,
  onExport,
  onUpgrade,
}: {
  exportType: ExportType;
  isLoading: boolean;
  isDisabled: boolean;
  isPaidUser: boolean;
  onExport: () => void;
  onUpgrade: () => void;
}) {
  const isFigma = exportType === 'figma';

  const getButtonText = () => {
    if (isLoading) {
      return isFigma ? 'Copying...' : 'Exporting...';
    }
    return isFigma ? 'Copy to Figma' : 'Export';
  };

  return (
    <div className='flex flex-col gap-2'>
      <Button
        variant='primary'
        size='sm'
        onClick={onExport}
        disabled={isDisabled || isLoading}
        className='-mt-3 flex w-full items-center justify-center'
      >
        {getButtonText()}
      </Button>
      {isFigma && !isPaidUser && (
        <>
          <hr className='-mx-[18px] mt-2 border-black/10' />
          <div className='mt-2 text-center text-sm text-muted-foreground'>
            Figma export is a paid feature.{' '}
            <button onClick={onUpgrade} className='font-semibold text-primary'>
              Upgrade
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ExportModalContent({
  flowId,
  chatIds,
  initialSelectedScreens,
}: {
  flowId: string;
  chatIds: string[];
  initialSelectedScreens?: string[];
}) {
  const [exportType, setExportType] = useState<ExportType>('figma');
  const [selectedScreens, setSelectedScreens] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasInitializedRef = useRef<boolean>(false);
  const {exportToFigma} = useExportToFigmaMulti(flowId);
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal} = usePaywallModal();
  const exportScreenshotsMutation =
    trpc.screens.exportScreenshots.useMutation();

  // Get structure status for all chats
  const {data: screensWithStructures} =
    trpc.screens.getScreensWithStructure.useQuery({
      screenIds: chatIds,
    });

  // Get structure names for all chat IDs
  const {screenNames} = useScreenNames(chatIds);

  // Reset initialization when initialSelectedScreens changes (modal reopened with different selection)
  const initialSelectedScreensKey = initialSelectedScreens?.join(',') ?? '';
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [initialSelectedScreensKey]);

  // Initialize selected screens when modal opens and data is available
  useEffect(() => {
    if (!hasInitializedRef.current && screensWithStructures !== undefined) {
      setSelectedScreens(
        initialSelectedScreens && initialSelectedScreens.length > 0
          ? initialSelectedScreens.filter((screenId) =>
              screensWithStructures.includes(screenId),
            )
          : [...screensWithStructures],
      );
      hasInitializedRef.current = true;
    }
  }, [hasInitializedRef, screensWithStructures, initialSelectedScreens]);

  const handleScreenSelect = (screenId: string) => {
    setSelectedScreens((prev) =>
      prev.includes(screenId)
        ? prev.filter((id) => id !== screenId)
        : [...prev, screenId],
    );
  };

  const handleSelectAllToggle = () => {
    if (!screensWithStructures) return;
    setSelectedScreens((prev) =>
      prev.length === screensWithStructures.length ? [] : screensWithStructures,
    );
  };

  const getSelectAllState = () => {
    if (
      selectedScreens.length === screensWithStructures?.length &&
      screensWithStructures.length > 0
    )
      return 'all';
    if (selectedScreens.length > 0) return 'some';
    return 'none';
  };

  const handleExportToFigma = async () => {
    setIsLoading(true);
    try {
      await exportToFigma(selectedScreens);
    } finally {
      setIsLoading(false);
    }
  };

  const exportImagesViaServer = async (imageFormat: 'png' | 'jpeg') => {
    console.log(
      `[ExportModal] export start: ${selectedScreens.length} screens, format=${imageFormat}`,
    );
    const {files} = await exportScreenshotsMutation.mutateAsync({
      flowId,
      screenIds: selectedScreens,
      formats: [imageFormat],
    });
    console.log(`[ExportModal] mutation returned ${files.length} file(s)`);
    if (files.length === 0) throw new Error('No screens could be exported.');

    const ext = imageFormat;
    const fileBlobs = await Promise.all(
      files.map(async (f) => {
        const screenName = screenNames.get(f.screenId) ?? f.screenId;
        console.log(`[ExportModal] fetching ${f.screenId} <- ${f.url}`);
        const res = await fetch(f.url);
        if (!res.ok) throw new Error(`fetch ${f.url} → ${res.status}`);
        const blob = await res.blob();
        console.log(
          `[ExportModal]   got ${screenName} (${(blob.size / 1024 / 1024).toFixed(2)}MB)`,
        );
        return {screenName, blob};
      }),
    );

    if (fileBlobs.length === 1) {
      const {screenName, blob} = fileBlobs[0];
      const link = document.createElement('a');
      link.download = `${screenName.replace(/[^\w-]/g, '')}.${ext}`;
      link.href = URL.createObjectURL(blob);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } else {
      const zip = new JSZip();
      for (const {screenName, blob} of fileBlobs) {
        const cleanFileName = screenName.replace(/[^\w-]/g, '');
        zip.file(`${cleanFileName}.${ext}`, blob);
      }
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {level: 6},
        platform: 'DOS',
      });
      const link = document.createElement('a');
      link.download = `banani-ui-export.zip`;
      link.href = URL.createObjectURL(content);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }

    console.log(`[ExportModal] export done: ${fileBlobs.length} files`);
    amplitudeService.logEvent('[Export button] export success', {
      flowId,
      ext: imageFormat,
      count: fileBlobs.length,
    });
  };

  const handleExportImages = async () => {
    if (exportType === 'figma') return;
    const imageFormat = exportType;

    try {
      setIsLoading(true);
      amplitudeService.logEvent('[Export button] download multiple images', {
        flowId,
        ext: imageFormat,
        count: selectedScreens.length,
      });

      await exportImagesViaServer(imageFormat);
    } catch (error) {
      console.error('[ExportModal] export error:', error);
      toast.error(
        `Export failed: ${error instanceof Error ? error.message : String(error)}`,
      );
      captureException(error);
      amplitudeService.logEvent('[Export button] export error', {
        flowId,
        ext: imageFormat,
        error: String(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (exportType === 'figma') {
      void handleExportToFigma();
    } else {
      void handleExportImages();
    }
  };

  return (
    <>
      <div className='flex flex-col gap-2.5'>
        <div className='text-sm font-semibold text-black'>Export format</div>
        <div className='flex gap-1 rounded-xl border border-black/15 p-1.5 shadow-sm'>
          <button
            onClick={() => setExportType('figma')}
            className={cn(
              'flex-1 rounded-lg px-1.5 py-1 text-sm font-semibold',
              exportType === 'figma'
                ? 'bg-primary text-white'
                : 'text-black/50 hover:text-black/70',
            )}
          >
            Figma
          </button>
          <button
            onClick={() => setExportType('png')}
            className={cn(
              'flex-1 rounded-lg px-1.5 py-1 text-sm font-semibold',
              exportType === 'png'
                ? 'bg-primary text-white'
                : 'text-black/50 hover:text-black/70',
            )}
          >
            PNG
          </button>
          <button
            onClick={() => setExportType('jpeg')}
            className={cn(
              'flex-1 rounded-lg px-1.5 py-1 text-sm font-semibold',
              exportType === 'jpeg'
                ? 'bg-primary text-white'
                : 'text-black/50 hover:text-black/70',
            )}
          >
            JPG
          </button>
        </div>
      </div>

      <div className='flex flex-col'>
        <div className='flex items-center'>
          <Checkbox
            checked={
              getSelectAllState() === 'all'
                ? true
                : getSelectAllState() === 'none'
                  ? false
                  : 'indeterminate'
            }
            onChange={handleSelectAllToggle}
            className='mr-2'
          />
          <div
            className='cursor-pointer select-none text-sm font-semibold text-black'
            onClick={handleSelectAllToggle}
          >
            {selectedScreens.length}{' '}
            {selectedScreens.length === 1 ? 'screen' : 'screens'} selected
          </div>
        </div>

        <hr className='-mx-[18px] mt-3 border-black/10' />

        <div className='relative'>
          <div className='max-h-64 overflow-y-auto pb-4 pt-3 scrollbar-hide'>
            {screensWithStructures === undefined ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className='mb-2 flex h-6 items-center'>
                    <div className='mr-2 h-5 w-5 animate-pulse rounded-md bg-muted' />
                    <div className='h-4 w-32 animate-pulse rounded bg-muted' />
                  </div>
                ))}
              </>
            ) : screensWithStructures.length === 0 ? (
              <div className='py-4 text-center text-sm text-muted-foreground'>
                No screens to export
              </div>
            ) : (
              screensWithStructures.map((chatId) => {
                const screenName =
                  screenNames.get(chatId) ||
                  `Screen ${chatIds.indexOf(chatId) + 1}`;
                const isSelected = selectedScreens.includes(chatId);

                return (
                  <div key={chatId} className='mb-2 flex h-6 items-center'>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleScreenSelect(chatId)}
                      className='mr-2'
                    />
                    <div
                      className='cursor-pointer select-none text-sm font-medium text-black'
                      onClick={() => handleScreenSelect(chatId)}
                    >
                      {screenName}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className='pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent' />
        </div>
      </div>

      <FigmaExportButton
        exportType={exportType}
        isLoading={isLoading}
        isDisabled={
          screensWithStructures === undefined ||
          selectedScreens.length === 0 ||
          screensWithStructures.length === 0
        }
        isPaidUser={effectiveSubscription?.isActive ?? false}
        onExport={handleExport}
        onUpgrade={() => openPaywallModal('export-to-figma')}
      />
    </>
  );
}

export const ExportModal = ({
  flowId,
  isVisible,
  onClose,
  initialSelectedScreens,
}: {
  flowId: string;
  isVisible: boolean;
  onClose: () => void;
  initialSelectedScreens?: string[];
}) => {
  const screensData = trpc.screens.getFlowScreens.useQuery(
    {flowId},
    {enabled: isVisible},
  ).data;
  const chatIds = useMemo(() => screensData?.map((s) => s.id), [screensData]);

  if (!isVisible) return null;

  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <ExportModalContent
          flowId={flowId}
          chatIds={chatIds ?? []}
          initialSelectedScreens={initialSelectedScreens}
        />
      </DialogContent>
    </Dialog>
  );
};
